import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import LoginSidebar from './LoginSidebar';
import LoginStepper from './LoginStepper';
import WelcomeMessage from './WelcomeMessage';
import Copyright from '../../utils/Copyright';
import '../index.css';

// The comp frames this as a 960x700 card; per product decision the login
// fills the viewport instead, keeping the comp's inner treatment (full-bleed
// sidebar with right-side rounded corners against the white content area).
const LAYOUT = {
  root: {
    display: 'flex',
    boxSizing: 'border-box',
    minHeight: '100vh',
    // Literal, not --zippedi-surface: that token goes dark under
    // [data-theme='dark'] while the form keeps the MUI light theme.
    background: '#fff',
  },
  content: {
    flex: 1,
    padding: '44px 52px 30px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  // Caps the fields at the comp's content width (~430px) on wide screens.
  formColumn: {
    flex: 1,
    width: '100%',
    maxWidth: '430px',
    display: 'flex',
    flexDirection: 'column',
  },
  // Vertical auto margins center the form between the top and the footer.
  formBlock: {
    margin: 'auto 0',
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    paddingTop: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  mobileFormColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  mobileFormBlock: {
    marginBottom: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
};

/**
 * Presentational shell for the login / password-recovery screens. Owns the
 * full-bleed sidebar, the centered form column and the footer (copyright + help
 * link). Screens pass their own view as
 * children; all sign-in / recovery logic stays outside.
 *
 * Below the sidebar breakpoint it swaps to the mobile shell: dark gradient
 * page, ZIPPEDI header, and the same form on a white bottom sheet.
 *
 * @param {string} [welcomeMessage] Per-app greeting resolved by heimdal-api.
 *   Typed into the sidebar on large screens (nothing when empty) and shown as
 *   a caption above the form when the sidebar is hidden, so the mobile WebView
 *   keeps it.
 * @param {string} helpUrl Href for the footer "need help" link.
 * @param {import('react').ReactNode} children Rendered inside the form block.
 */
export default function LoginLayout({
  stage = null,
  welcomeMessage,
  helpUrl,
  children,
}) {
  const { t } = useTranslation();
  // Same 1200px the form views use to swap their logo for a title; showing the
  // sidebar earlier would put the wordmark next to that logo. Drops to 900px
  // with them.
  const isLargeScreen = useMediaQuery('(min-width: 1200px)');

  const footer = (
    <div style={LAYOUT.footer}>
      <Copyright />
      <Typography align="center" variant="body2">
        <Link
          sx={{
            color: 'var(--zippedi-accent)',
            textDecorationColor: 'var(--zippedi-accent)',
          }}
          href={helpUrl}>
          {t('heimdal_app.Login.need_help')}
        </Link>
      </Typography>
    </div>
  );

  if (!isLargeScreen) {
    return (
      <div className="login-shell">
        <div className="login-shell__decor" aria-hidden="true">
          <span className="login-shell__blob login-shell__blob--top" />
        </div>
        <header className="login-shell__header">
          <span className="login-shell__wordmark">ZIPPEDI</span>
        </header>
        <div className="login-shell__sheet">
          <div style={LAYOUT.mobileFormColumn}>
            {welcomeMessage ? (
              <WelcomeMessage message={welcomeMessage} />
            ) : null}
            <div style={LAYOUT.mobileFormBlock}>{children}</div>
            {footer}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={LAYOUT.root}>
      <LoginSidebar message={welcomeMessage}>
        {stage !== null ? <LoginStepper activeStage={stage} /> : null}
      </LoginSidebar>
      <div style={LAYOUT.content}>
        <div style={LAYOUT.formColumn}>
          <div style={LAYOUT.formBlock}>{children}</div>
          {footer}
        </div>
      </div>
    </div>
  );
}

LoginLayout.propTypes = {
  stage: PropTypes.number,
  welcomeMessage: PropTypes.string,
  helpUrl: PropTypes.string.isRequired,
  children: PropTypes.node,
};
